<template>
  <div>
    <div class="jumbotron">
      <ol class="breadcrumb">
        <li class="breadcrumb-item"><a href="#/home">Home</a></li>
        <li class="breadcrumb-item"><a href="#/sync">Sync</a></li>
        <li class="breadcrumb-item active">Guide</li>
      </ol>
    </div>
    <ul class="nav nav-tabs">
      <li class="nav-item">
        <a class="nav-link active" data-toggle="tab" href="#guide">Guide</a>
      </li>
      <li class="nav-item">
        <a class="nav-link" data-toggle="tab" href="#relationships">Relationships</a>
      </li>
      <li class="nav-item">
        <a class="nav-link" data-toggle="tab" href="#user-helium">Single device per user</a>
      </li>
      <li class="nav-item">
        <a class="nav-link" data-toggle="tab" href="#user-journey">Multiple devices per user</a>
      </li>
    </ul>
    <div class="tab-content">
      <div class="tab-pane active show" id="guide">
        <div class="row">
          <div class="col">
            <legend>Introduction:</legend>
            <p>The Helium Journey middleware provides bidirectional data synchronisation between a Helium app's database and a Journey app's database. See the other tabs for code examples.</p>
            <fieldset>
              <legend>Data model:</legend>
              <p>In order to facilitate data sync between the two platforms the Helium Journey middleware requires the data model of the Helium and Joureny app to match. Persistent objects declared must exist the same on both sides such as the object name, attributes and relationship names.</p>
              <legend>Primitive Data Types:</legend>
              <p>The data models declared for an integrated Helium Journey application, for the objects that are to be synchronised, must be exactly the same and of equivalent types.</p>
              <p>Supported Data Types:</p>
              <table class="table table-sm table-hover">
                <thead>
                  <tr>
                    <th scope="col"></th>
                    <th scope="col">Whole number</th>
                    <th scope="col">Textual value</th>
                    <th scope="col">Floating point number</th>
                    <th scope="col">Boolean value</th>
                    <th scope="col">Date value</th>
                    <th scope="col">Date and time value</th>
                    <th scope="col">Photo value</th>
                    <th scope="col">Signature value</th>
                    <th scope="col">Attachment value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Helium</th>
                    <td>int</td>
                    <td>string</td>
                    <td>decimal</td>
                    <td>bool</td>
                    <td>date</td>
                    <td>datetime</td>
                    <td>blob</td>
                    <td>blob</td>
                    <td>blob</td>
                  </tr>
                  <tr>
                    <th scope="row">Journey</th>
                    <td>integer</td>
                    <td>text</td>
                    <td>number</td>
                    <td>boolean</td>
                    <td>date</td>
                    <td>datetime</td>
                    <td>photo</td>
                    <td>signature</td>
                    <td>attachment</td>
                  </tr>
                </tbody>
              </table>
              <p>Unsupported Data Types:</p>
              <table class="table table-sm table-hover">
                <thead>
                  <tr>
                    <th scope="col"></th>
                    <th scope="col">Void</th>
                    <th scope="col">Location</th>
                    <th scope="col">Single Choice</th>
                    <th scope="col">Single Choice Integer</th>
                    <th scope="col">Multiple Choice</th>
                    <th scope="col">Multiple Choice Integer</th>
                    <th scope="col">Tracker</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Helium</th>
                    <td>void (as method return type only)</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                  <tr>
                    <th scope="row">Journey</th>
                    <td>-</td>
                    <td>location</td>
                    <td>single-choice</td>
                    <td>single-choice-integer</td>
                    <td>multiple-choice</td>
                    <td>multiple-choice-integer</td>
                    <td>track</td>
                  </tr>
                </tbody>
              </table>
              <p>The unsupported data types on Joureny may be used however only in the context of the view javascript and when using the <a href="https://docs.journeyapps.com/docs/app-database#section-localdb">LocalDB</a></p>
              <legend>Enums:</legend>
              <p>Helium and Journey handle enum types differently and due to this a Journey enum is not directly compatable with a Helium enum. Enums can however be used in an integrated application by using enums only on Helium. On Journey use the datatype type text, values persisted in the text format on Journey need to be a valid enum value as declared on the Helium model.</p>
              <p>Journey enums can be used on the Journey app as long as they are used only in the view javascript or when using the <a href="https://docs.journeyapps.com/docs/app-database#section-localdb">LocalDB</a></p>
              <legend>Relationships:</legend>
              <p>OneToOne and ManyToOne relationships are supported provided the relationship names are matching on Helium and Journey.</p>
              <p>ManyToMany and OneToOne relationships are not supported on Journey and are therefore objects using these annotations should also be skipped from syncing by also adding the @NotTracked annotation to the persistent object</p>
              <legend>Ignoring Certain Objects:</legend>
              <ul>
                <li>To skip an object from being processed from Helium to Journey the <a href="https://wiki.mezzanineware.com/display/HTUT/Data+Types#DataTypes-@NotTracked">@NotTracked annotation</a> can be used.</li>
                <li>To skip an object from being processed from Journey to Helium the <a href="https://docs.journeyapps.com/docs/app-database#section-localdb">LocalDB</a> feature can be used.</li>
                <li>If for some reason the annotation or LocalDB cannot be used objects can be specifically skipped on the integration configuration.</li>
                <li>When an object is halting integration because it cannot be synced to either Helium or Journey then it's type can be explicilty skipped until sync has advanced past that version</li>
              </ul>
              <legend>Special Attribute Names:</legend>
              If for some reason on Journey side someone has defined an attribute name that is reserved on Helium, an alternative attribute name must be used on both Helium and Journey.
              <table class="table table-sm table-hover">
                <thead>
                  <tr>
                    <th scope="col">Reserved attribute / column name</th>
                    <th scope="col">Recommended naming on both Helium and Journey</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>date</td>
                    <td>creation_date</td>
                  </tr>
                </tbody>
              </table>
              <legend>Attribute names ending in "_id"</legend>
              <p>Do not create attributes on Helium ending in "_id".  This is seen as a relationship on Journey's side.</p>
              <legend>Object names in CAPS</legend>
              <p>Do not use Journey object names in all CAPS.  It will not be handled correctly on Helium's side.</p>
              <legend>Cascade in relationships</legend>
              <p>When you delete an object on Journey, and some other object depends on it (in a many-to-one relationship), then Journey has a cascade policy and will subsequently nullify the other object's owner reference. Helium as of 1.3.5 also has a "on cascade set null" policy. As an example, if you have a Farmer object with a many to one relationship to a Country object e.g. South Africa.  If the country South Africa gets deleted, then the country_id gets set to null on Journey, and likewise the column country_fk gets set to null on Helium</p>
              <legend>Resources:</legend>
              <p>See <a href="https://wiki.mezzanineware.com/display/HTUT/Data+Types">Helium data type docs</a> and <a href="https://docs.journeyapps.com/v4/reference#section-field-types">Journey data type docs</a> for more info data types.</p>
            </fieldset>
          </div>
        </div>
      </div>
      <div class="tab-pane fade" id="relationships">
        <div class="row">
          <div class="col-6">
            <legend>Helium:</legend>
            <text-editor
              v-bind:contentProp="fkHeliumContent"
              v-bind:width="textEditorWidthUser"
              v-bind:height="textEditorHeightUser"
              v-bind:readonly="textEditorReadOnly"
              v-bind:scrollToLine="textEditorScrollToLine"
              v-bind:lang="textEditorJava">
            </text-editor>
          </div>
          <div class="col-6">
            <legend>Journey:</legend>
            <text-editor
              v-bind:contentProp="fkJourneyContent"
              v-bind:width="textEditorWidthUser"
              v-bind:height="textEditorHeightUser"
              v-bind:readonly="textEditorReadOnly"
              v-bind:scrollToLine="textEditorScrollToLine"
              v-bind:lang="textEditorXml">
            </text-editor>
          </div>
        </div>
      </div>
      <div class="tab-pane fade" id="user-helium">
        <div class="row">
          <div class="col-6">
            <legend>Helium:</legend>
            <text-editor
              v-bind:contentProp="userHeliumContent"
              v-bind:width="textEditorWidthUser"
              v-bind:height="textEditorHeightUser"
              v-bind:readonly="textEditorReadOnly"
              v-bind:scrollToLine="textEditorScrollToLine"
              v-bind:lang="textEditorJava">
            </text-editor>
          </div>
          <div class="col-6">
            <legend>Journey:</legend>
            <text-editor
              v-bind:contentProp="userJourneyContent"
              v-bind:width="textEditorWidthUser"
              v-bind:height="textEditorHeightUser"
              v-bind:readonly="textEditorReadOnly"
              v-bind:scrollToLine="textEditorScrollToLine"
              v-bind:lang="textEditorXml">
            </text-editor>
          </div>
        </div>
      </div> <!-- user-helium tab -->
      <div class="tab-pane fade" id="user-journey">
        <div class="row">
          <div class="col-6">
            <legend>Helium:</legend>
            <text-editor
              v-bind:contentProp="userHeliumV2Content"
              v-bind:width="textEditorWidthUser"
              v-bind:height="textEditorHeightUser"
              v-bind:readonly="textEditorReadOnly"
              v-bind:scrollToLine="textEditorScrollToLine"
              v-bind:lang="textEditorJava">
            </text-editor>
          </div>
          <div class="col-6">
            <legend>Journey:</legend>
            <text-editor
              v-bind:contentProp="userJourneyV2Content"
              v-bind:width="textEditorWidthUser"
              v-bind:height="textEditorHeightUser"
              v-bind:readonly="textEditorReadOnly"
              v-bind:scrollToLine="textEditorScrollToLine"
              v-bind:lang="textEditorXml">
            </text-editor>
          </div>
        </div>
      </div> <!-- user-journey tab -->
    </div> <!-- tab-content -->
  </div> <!-- main-div -->
</template>

<script>
import TextEditor from '@/components/TextEditor.vue'
export default {
  components: {
    TextEditor
  },
  data () {
    return {
      textEditorWidthUser: 0,
      textEditorHeightUser: 0,
      textEditorReadOnly: true,
      textEditorScrollToLine: 0,
      textEditorJava: 'java',
      textEditorXml: 'xml',
      userHeliumContent: '/* Helium persistant object primary user declaration example */\n' +
      'persistent object MobileUser {\n\n' +
      '\t@defaultValidator("error.required_field")\n' +
      '\tstring firstnames;\n\n' +
      '\t// .. other attributes\n\n' +
      '\t// Journey Enrollment\n' +
      '\tstring enrollment_allowed_access;\n' +
      '\tstring enrollment_enrolled;\n' +
      '\tstring enrollment_journey_launcher_version;\n' +
      '\tstring enrollment_device_os;\n' +
      '\tstring enrollment_device_model;\n' +
      '\tstring enrollment_last_connected;\n' +
      '\tstring enrollment_barcode;\n' +
      '\tstring enrollment_url;\n' +
      '\tstring enrollment_perform_re_enrollment;\n' +
      '\tdatetime enrollment_re_enrollment_last_processed;\n' +
      '\tbool enrollment_generate_enrollment_code;\n' +
      '\tstring enrollment_code;\n\n' +
      '\t// ... relationships\n\n' +
      '}',
      userJourneyContent: '<!-- Journey V4 model primary user declaration example -->\n' +
      '<model name="mobileuser" label="Mobile User">\n' +
      '\t<field name="firstnames" label="Firstnames" type="text"/>\n\n' +
      '\t<!-- ... other attributes -->\n\n' +
      '\t<field name="enrollment_allowed_access" label="Enrollment allowed access" type="text"/>\n' +
      '\t<field name="enrollment_enrolled" label="Enrollment enrolled" type="text"/>\n' +
      '\t<field name="enrollment_journey_launcher_version" label="Enrollment Journey launcher version" type="text"/>\n' +
      '\t<field name="enrollment_device_os" label="Enrollment device OS" type="text"/>\n' +
      '\t<field name="enrollment_device_model" label="Enrollment device model" type="text"/>\n' +
      '\t<field name="enrollment_last_connected" label="Enrollment last connected" type="text"/>\n' +
      '\t<field name="enrollment_barcode" label="Enrollment barcode" type="text"/>\n' +
      '\t<field name="enrollment_url" label="Enrollment URL" type="text"/>\n' +
      '\t<field name="enrollment_perform_re_enrollment" label="Enrollment perform re-enrollment" type="boolean"/>\n' +
      '\t<field name="enrollment_re_enrollment_last_processed" label="Enrollment re-enrollment last processed" type="text"/>\n' +
      '\t<field name="enrollment_generate_enrollment_code" label="Enrollment Generate Enrollment Code" type="boolean"/>' +
      '\t<field name="enrollment_code" label="Enrollment Code" type="text"/>' +
      '\t<!-- ... relationships -->\n\n' +
      '\t<display>{firstnames}</display>\n' +
      '</model>',
      userHeliumV2Content: '/* Helium persistant object primary user declaration example */\n' +
      'persistent object mobileuser {\n\n' +
      '\t@defaultValidator("error.required_field")\n' +
      '\tstring firstnames;\n\n' +
      '\t// .. other attributes\n\n' +
      '\t// Journey Enrollment\n' +
      '\tdatetime last_connected_at;\n' +
      '\tbool enrolled;\n' +
      '\tbool locked;\n\n' +
      '\tbool enrollment_generate_enrollment_code; // set this as true to generate enrollmentCode\n' +
      '\tstring enrollment_code;\n\n' +
      '\t// ... relationships\n\n' +
      '}\n\n' +
      '/* Helium persistant object user session declaration example */\n' +
      'persistent object mobileusersession {\n\n' +
      '\tstring state;\n' +
      '\tdatetime last_authenticated_at;\n' +
      '\tdatetime created_at;\n' +
      '\tdatetime signed_out_at;\n' +
      '\tdatetime last_connected_at;\n' +
      '\tbool perform_re_authenticate; // set this as true to force this user to reauthenticate\n' +
      '\tdatetime re_authenticate_last_processed;\n' +
      '\tstring label;\n\n' +
      '\t@ManyToOne\n' +
      '\tmobileuser mobileusersession_mobileuser via mobileuser_mobileusersession;\n\n' +
      '}\n\n' +
      '/* Helium persistant object user session container declaration example */\n' +
      '@NotTracked\n' +
      'persistent object mobileusersessioncontainer {\n' +
      '\tstring version;\n' +
      '\tstring bundle_id;\n' +
      '\tstring platform;\n\n' +
      '\t@ManyToOne\n' +
      '\tmobileusersession mobileusersessioncontainer_mobileusersession via mobileusersession_mobileusersessioncontainer;\n' +
      '}\n',
      userJourneyV2Content: '<!-- Journey V4 model primary user declaration example -->\n' +
      '<model name="mobileuser" label="Mobile User">\n' +
      '\t<field name="firstnames" label="Firstnames" type="text"/>\n\n' +
      '\t<!-- ... other attributes -->\n\n' +
      '\t<field name="last_connected_at" label="Last Connected At" type="datetime"/>\n' +
      '\t<field name="enrolled" label="Enrolled" type="boolean"/>\n' +
      '\t<field name="locked" label="Locked" type="boolean"/>\n' +
      '\t<field name="generate_enrollment_code" label="Generate Enrollment Code" type="boolean"/>\n' +
      '\t<field name="enrollment_code" label="Generate Code" type="text"/>\n' +
      '\t<!-- ... relationships -->\n\n' +
      '\t<display>{firstnames}</display>\n' +
      '</model>',
      fkHeliumContent: '/* Helium persistant object example of parent in relationship */\n' +
      'persistent object parent {\n' +
      '\tstring key;\n' +
      '\tdatetime dateRegistered;\n\n' +
      '\t@OneToMany\n' +
      '\tchild parent_child via child_parent;\n' +
      '}\n\n' +
      '/* Helium persistant object example of child in relationship */\n' +
      'persistent object child {\n\n' +
      '\tstring key;\n' +
      '\tdatetime dateRegistered;\n\n' +
      '\t@ManyToOne\n' +
      '\tparent child_parent via parent_child;\n\n' +
      '}',
      fkJourneyContent: '<!-- Journey V4 model example of parent in relationship -->\n' +
      '<model name="parent" label="Parent">\n' +
      '\t<field name="key" label="Key" type="text"/>\n' +
      '\t<field name="dateRegistered" label="Date Registered" type="datetime"/>\n\n' +
      '\t<has-many name="children" inverse-of="child_parent" model="child"/>\n' +
      '\t<display>{key}</display>\n' +
      '</model>\n\n' +
      '<!-- Journey V4 model example of child in relationship -->\n' +
      '<model name="child" label="Child">\n' +
      '\t<field name="key" label="Key" type="text"/>\n' +
      '\t<field name="dateRegistered" label="Date Registered" type="datetime"/>\n' +
      '\t<belongs-to name="child_parent" model="parent"/>\n' +
      '\t<display>{key} {child_parent}</display>\n' +
      '</model>'
    }
  },
  methods: {
    resizeEditor: function () {
      let vm = this
      let widthValue = Math.round(window.innerWidth * 47 / 100)
      vm.textEditorWidthUser = widthValue
      vm.textEditorHeightUser = 500
    }
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.resizeEditor)
  },
  mounted: function () {
    this.resizeEditor()
    window.addEventListener('resize', this.resizeEditor)
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// SyncGuide.vue?654dfec4